import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const FreePlaylist: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="bg-gray-100 dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>{t('metadata.freeSources.title')}</title>
        <meta name="description" content={t('metadata.freeSources.description')} />
        <meta name="keywords" content="free IPTV playlist, legal M3U sources, free TV channels, IPTV streaming, HLS player, M3U playlist finder, free streaming TV, web IPTV player" />
        <link rel="canonical" href="https://iptvplayer.stream/free-playlist" />
      </Helmet>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8 text-gray-800 dark:text-white">Free IPTV Playlists: Stream Thousands of Legal Channels</h1>
        
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700 dark:text-gray-200">Access Free TV Channels Worldwide</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Discover how to stream thousands of free, legal IPTV channels directly in your browser with IPTVPlayer.stream. 
            Our web-based M3U player makes it easy to access free television content from around the world, supporting HLS, 
            DASH, and other streaming formats.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6 text-gray-700 dark:text-gray-200">Legal Free IPTV Sources</h2>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200">1. GitHub Free TV Project</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              The <a 
                href="https://github.com/Free-TV/IPTV/tree/master/playlists" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >Free TV project on GitHub</a> maintains a curated collection of legal, freely available IPTV channels. 
              This open-source playlist is regularly updated and can be directly imported into IPTVPlayer.stream for 
              instant streaming.
            </p>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-4">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                💡 Tip: Import the playlist URL directly into IPTVPlayer.stream using our "Import from URL" feature.
                Browse the <a 
                  href="https://github.com/Free-TV/IPTV/tree/master/playlists" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-200 underline"
                >available playlists by country</a> and copy the raw URL of your desired playlist.
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200">2. Public Broadcast Channels</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Many public broadcasters offer their content through free M3U playlists. These channels typically include:
            </p>
            <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-2">
              <li>News channels from various countries</li>
              <li>Public television networks</li>
              <li>Educational content</li>
              <li>Regional broadcasts</li>
            </ul>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-200">3. Legal Streaming Sources</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Many content providers offer their streams legally and free of charge. IPTVPlayer.stream makes it easy to:
            </p>
            <ul className="list-disc ml-6 text-gray-600 dark:text-gray-300 space-y-2">
              <li>Import M3U playlists directly via URL</li>
              <li>Organize channels into custom groups</li>
              <li>Save favorite channels for quick access</li>
              <li>Stream content in high quality with our optimized HLS player</li>
            </ul>
          </div>
        </section>

        <section className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-12">
          <h2 className="text-2xl font-semibold mb-4 text-blue-800 dark:text-blue-300">Getting Started with Free IPTV</h2>
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              To start streaming free IPTV channels with IPTVPlayer.stream:
            </p>
            <ol className="list-decimal ml-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li>Visit IPTVPlayer.stream in your web browser</li>
              <li>Click "Import Playlist" and choose your import method</li>
              <li>Paste your M3U playlist URL or upload your playlist file</li>
              <li>Start streaming instantly - no downloads required!</li>
            </ol>
          </div>
        </section>

        <section className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-6 mb-12">
          <h2 className="text-2xl font-semibold mb-4 text-yellow-800 dark:text-yellow-300">Legal Considerations</h2>
          <p className="text-gray-700 dark:text-gray-300">
            When using free IPTV playlists, ensure you're only accessing legally available content. IPTVPlayer.stream 
            is designed for streaming legal content and should not be used for unauthorized access to copyrighted material.
          </p>
        </section>

        <section className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-200">Ready to Start Streaming?</h2>
          <p className="text-gray-600 dark:text-gray-300">
            Explore our{' '}
            <Link 
              to="/m3u-hls-player-features" 
              className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
            >
              {t('footer.features', 'Features Guide')}
            </Link>
            {' '}to learn more about IPTVPlayer.stream's capabilities, or{' '}
            <Link 
              to="/contact" 
              className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
            >
              contact our support team
            </Link>
            {' '}if you need help getting started with free IPTV streaming.
          </p>
        </section>
      </main>
    </div>
  );
};

export default FreePlaylist; 